'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Menu, X, ArrowRight, AlertCircle
} from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';

interface NavbarProps {
  showNewsAlert?: boolean;
  setShowNewsAlert?: (show: boolean) => void;
}

const Navbar = ({ showNewsAlert = false, setShowNewsAlert }: NavbarProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    router.push('/signup');
  };

  return (
    <>
      {/* News Alert Banner */}
      {pathname === '/' && showAlert && (
        <div className="fixed top-0 left-0 right-0 bg-green-600 text-white py-2 px-4 z-50">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p className="text-sm md:text-base">
                <span className="font-bold">Big News!</span> Yalfin is now Yalfin Lite and completely FREE for everyone!
              </p>
            </div>
            {setShowAlert && (
              <button
                onClick={() => setShowAlert(false)}
                className="text-white hover:text-gray-200"
                aria-label="Dismiss news alert"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed w-full z-40 transition-all duration-300 ${isScrolled ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm' : 'bg-transparent'}`} style={{ top: showAlert && pathname === '/' ? '40px' : '0' }}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
              <svg width="116" height="46" viewBox="0 0 116 46" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 md:h-10 w-auto">
                <path d="M4.45824 0H111.713L112.742 0.343284L113.771 1.02985L114.628 1.94527L115.314 3.08955L115.829 4.46269L116 5.37811V40.393L115.657 41.995L114.885 43.597L114.2 44.5124L113.085 45.4279L112.142 45.8856L111.027 46H4.97265L3.77236 45.7711L2.6578 45.199L1.80044 44.398L1.11456 43.4826L0.514412 42.2239L0.171471 40.9652L0 39.0199V7.32338L0.0857354 5.37811L0.428677 4.00497L0.943089 2.74627L1.71471 1.60199L2.57206 0.800995L3.51515 0.228856L4.45824 0Z" className="fill-black dark:fill-white" />
                <path d="M29.332 15.064C28.252 18.712 27.124 22.132 25.948 25.324C24.772 28.516 23.488 31.612 22.096 34.612C21.592 35.692 21.088 36.604 20.584 37.348C20.08 38.116 19.528 38.74 18.928 39.22C18.328 39.724 17.644 40.084 16.876 40.3C16.132 40.54 15.256 40.66 14.248 40.66C13.408 40.66 12.628 40.576 11.908 40.408C11.212 40.264 10.636 40.096 10.18 39.904L11.116 35.62C11.668 35.812 12.16 35.944 12.592 36.016C13.024 36.088 13.48 36.124 13.96 36.124C14.92 36.124 15.652 35.86 16.156 35.332C16.684 34.828 17.128 34.12 17.488 33.208C16.264 30.808 15.04 28.12 13.816 25.144C12.592 22.144 11.44 18.784 10.36 15.064H16.048C16.288 16 16.564 17.02 16.876 18.124C17.212 19.204 17.56 20.308 17.92 21.436C18.28 22.54 18.64 23.62 19 24.676C19.384 25.732 19.744 26.692 20.08 27.556C20.392 26.692 20.716 25.732 21.052 24.676C21.388 23.62 21.712 22.54 22.024 21.436C22.36 20.308 22.672 19.204 22.96 18.124C23.272 17.02 23.548 16 23.788 15.064H29.332ZM39.0919 30.256C39.6199 30.256 40.1239 30.244 40.6039 30.22C41.0839 30.196 41.4679 30.16 41.7559 30.112V26.044C41.5399 25.996 41.2159 25.948 40.7839 25.9C40.3519 25.852 39.9559 25.828 39.5959 25.828C39.0919 25.828 38.6119 25.864 38.1559 25.936C37.7239 25.984 37.3399 26.092 37.0039 26.26C36.6679 26.428 36.4039 26.656 36.2119 26.944C36.0199 27.232 35.9239 27.592 35.9239 28.024C35.9239 28.864 36.1999 29.452 36.7519 29.788C37.3279 30.1 38.1079 30.256 39.0919 30.256ZM38.6599 14.56C40.2439 14.56 41.5639 14.74 42.6199 15.1C43.6759 15.46 44.5159 15.976 45.1399 16.648C45.7879 17.32 46.2439 18.136 46.5079 19.096C46.7719 20.056 46.9039 21.124 46.9039 22.3V33.46C46.1359 33.628 45.0679 33.82 43.6999 34.036C42.3319 34.276 40.6759 34.396 38.7319 34.396C37.5079 34.396 36.3919 34.288 35.3839 34.072C34.3999 33.856 33.5479 33.508 32.8279 33.028C32.1079 32.524 31.5559 31.876 31.1719 31.084C30.7879 30.292 30.5959 29.32 30.5959 28.168C30.5959 27.064 30.8119 26.128 31.2439 25.36C31.6999 24.592 32.2999 23.98 33.0439 23.524C33.7879 23.068 34.6399 22.744 35.5999 22.552C36.5599 22.336 37.5559 22.228 38.5879 22.228C39.2839 22.228 39.8959 22.264 40.4239 22.336C40.9759 22.384 41.4199 22.456 41.7559 22.552V22.048C41.7559 21.136 41.4799 20.404 40.9279 19.852C40.3759 19.3 39.4159 19.024 38.0479 19.024C37.1359 19.024 36.2359 19.096 35.3479 19.24C34.4599 19.36 33.6919 19.54 33.0439 19.78L32.3599 15.46C32.6719 15.364 33.0559 15.268 33.5119 15.172C33.9919 15.052 34.5079 14.956 35.0599 14.884C35.6119 14.788 36.1879 14.716 36.7879 14.668C37.4119 14.596 38.0359 14.56 38.6599 14.56ZM59.3144 34.36C57.7544 34.336 56.4824 34.168 55.4984 33.856C54.5384 33.544 53.7704 33.112 53.1944 32.56C52.6424 31.984 52.2584 31.3 52.0424 30.508C51.8504 29.692 51.7544 28.78 51.7544 27.772V6.928L57.1184 6.064V26.692C57.1184 27.172 57.1544 27.604 57.2264 27.988C57.2984 28.372 57.4304 28.696 57.6224 28.96C57.8384 29.224 58.1384 29.44 58.5224 29.608C58.9064 29.776 59.4224 29.884 60.0704 29.932L59.3144 34.36ZM71.353 6.064C72.361 6.064 73.189 6.148 73.837 6.316C74.509 6.484 75.073 6.664 75.529 6.856L74.593 11.068C74.209 10.9 73.777 10.78 73.297 10.708C72.841 10.636 72.373 10.6 71.893 10.6C71.245 10.6 70.693 10.696 70.237 10.888C69.805 11.056 69.457 11.296 69.193 11.608C68.953 11.92 68.773 12.292 68.653 12.724C68.557 13.156 68.509 13.624 68.509 14.128V15.064H75.133V19.528H68.509V34H63.145V14.056C63.145 11.608 63.829 9.664 65.197 8.224C66.589 6.784 68.641 6.064 71.353 6.064ZM83.593 34H78.229V15.064H83.593V34ZM84.097 9.592C84.097 10.576 83.773 11.356 83.125 11.932C82.501 12.484 81.757 12.76 80.893 12.76C80.029 12.76 79.273 12.484 78.625 11.932C78.001 11.356 77.689 10.576 77.689 9.592C77.689 8.608 78.001 7.84 78.625 7.288C79.273 6.712 80.029 6.424 80.893 6.424C81.757 6.424 82.501 6.712 83.125 7.288C83.773 7.84 84.097 8.608 84.097 9.592ZM88.6333 15.712C89.5453 15.448 90.7213 15.208 92.1613 14.992C93.6013 14.752 95.1133 14.632 96.6973 14.632C98.3053 14.632 99.6373 14.848 100.693 15.28C101.773 15.688 102.625 16.276 103.249 17.044C103.873 17.812 104.317 18.724 104.581 19.78C104.845 20.836 104.977 22.012 104.977 23.308V34H99.6133V23.956C99.6133 22.228 99.3853 21.004 98.9293 20.284C98.4733 19.564 97.6213 19.204 96.3733 19.204C95.9893 19.204 95.5813 19.228 95.1493 19.276C94.7173 19.3 94.3333 19.336 93.9973 19.384V34H88.6333V15.712Z" className="fill-white dark:fill-black" />
              </svg>
              <Badge className="bg-green-600 text-white">LITE</Badge>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link href="/news">
                <button className="text-gray-800 hover:text-black dark:text-gray-200 dark:hover:text-white">
                  News
                </button>
              </Link>
              <Link href="/pricing">
                <button className="text-gray-800 hover:text-black dark:text-gray-200 dark:hover:text-white">
                  Pricing
                </button>
              </Link>
              <Link href="/about">
                <button className="text-gray-800 hover:text-black dark:text-gray-200 dark:hover:text-white">
                  About
                </button>
              </Link>
              {!user ? (
                <Link href="/login">
                  <button className="text-gray-800 hover:text-black dark:text-gray-200 dark:hover:text-white">
                    Login
                  </button>
                </Link>
              ) : (
                <Link href="/dashboard">
                  <Button variant="outline" size="sm">Dashboard</Button>
                </Link>
              )}
              <Button
                className="bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 rounded-full px-6"
                onClick={handleGetStarted}
              >
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden text-gray-800 dark:text-gray-200"
              aria-label="Open mobile menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Menu Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="absolute right-0 top-0 h-full w-64 bg-white dark:bg-gray-900 p-4 shadow-xl"
          >
            <div className="flex justify-end mb-8">
              <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Close mobile menu">
                <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </button>
            </div>
            <nav className="space-y-4 flex flex-col h-full">
              {/* Links */}
              <Link href="/news" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">News</Link>
              <Link href="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Pricing</Link>
              <Link href="/about" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">About</Link>
              {!user ? (
                <Link href="/login" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Login</Link>
              ) : (
                <Link href="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Dashboard</Link>
              )}
              {/* Button at bottom */}
              <div className="mt-auto pt-4 border-t dark:border-gray-700">
                <Button
                  className="w-full bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
                  onClick={() => {
                    handleGetStarted();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </nav>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default Navbar; 